import { useEffect, useRef, useState, type FormEvent } from 'react';
import { describeAcquisition, type TenantAcquisition } from '@/lib/acquisition';
import { Link, useParams } from 'react-router';
import {
  BadgePercent, Ban, Bot, CalendarPlus, CreditCard, DoorClosed, DoorOpen, ExternalLink,
  HandCoins, KeyRound, PackageOpen, PackageSearch, Power, Store, Wallet,
} from 'lucide-react';
import { api, cancelTenantSubscription, cancelTenantSubscriptionUrl, describeError } from '@/lib/api';
import { useIdempotencyKey } from '@/lib/idempotency';
import { stepUpOptions, useStepUpDialog, type StepUpProof } from '@/lib/step-up';
import { stepUpTarget } from '@/lib/passkeys';
import { StepUpDialog } from '@/components/StepUpDialog';
import { useAuthStore, useHasPlatformPermission } from '@/stores/auth';
import { Avatar, Badge, StatCard } from '@/components/ui';
import { CopyButton } from '@/components/CopyButton';
import { TenantPlanSection } from '@/components/TenantPlanSection';
import { TenantAiUnitsSection } from '@/components/TenantAiUnitsSection';
import { TenantPrintingSection } from '@/components/TenantPrintingSection';
import { subscriptionBadge } from '@/components/badge-labels';

interface BillingSummary {
  subscriptionId: string | null;
  asaasSubscriptionId: string | null;
  status: string;
  value: number | null;
  nextDueDate: string | null;
}

interface TenantAddress {
  street: string;
  number: string;
  complement: string | null;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
}

interface TenantDetail {
  id: string;
  tradeName: string;
  ownerName: string;
  slug: string;
  phone: string;
  isActive: boolean;
  subscriptionStatus: string;
  trialExpiresAt: string | null;
  createdAt: string;
  orderCount: number;
  billing: BillingSummary | null;
  menuUrl: string;
  menuHost: string;
  ownerEmail: string | null;
  cpfMasked: string | null;
  cnpjMasked: string | null;
  address: TenantAddress;
  lastOrderAt: string | null;
  orderCountLast30Days: number;
  isStoreOpen: boolean;
  /** Origem de campanha do cadastro; null = acesso direto. */
  acquisition: TenantAcquisition | null;
}

/** CEP só chega em dígitos; formatar aqui evita ler "01000000" em voz alta. */
function formatZip(zip: string): string {
  const digits = zip.replace(/\D/g, '');
  return digits.length === 8 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : zip;
}

function formatAddress(a: TenantAddress): string {
  const line = [a.street, a.number].filter(Boolean).join(', ');
  const withComplement = a.complement ? `${line} — ${a.complement}` : line;
  return [withComplement, a.neighborhood, `${a.city}/${a.state}`, formatZip(a.zipCode)]
    .filter(Boolean)
    .join(' · ');
}

export function TenantDetailPage() {
  const { tenantId } = useParams();
  const isAdmin = useAuthStore((s) => s.operator?.role) === 'Admin';
  // Por permissão, não por papel: o SupportL2 também estende trial (C1).
  const canExtendTrial = useHasPlatformPermission('platform.trial.extend');
  const canGrantConcession = useHasPlatformPermission('platform.concessions.write');
  const canDelegateToAgent = canExtendTrial || canGrantConcession;
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Falha da recarga depois de uma ação (o cancelamento da assinatura): a página fica de pé, com o
  // aviso de sucesso da ação, e o erro aparece num banner. Só a carga inicial troca a página pelo erro.
  const [reloadError, setReloadError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const loadedTenantId = useRef<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    api.get<TenantDetail>(`/platform/tenants/${tenantId}`)
      .then(({ data }) => {
        if (cancelled) return;
        loadedTenantId.current = tenantId;
        setTenant(data);
        setReloadError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (loadedTenantId.current === tenantId) setReloadError(describeError(err));
        else setError(describeError(err));
      });
    return () => { cancelled = true; };
  }, [tenantId, reloadTick]);

  if (error) return <div className="error-banner">{error}</div>;
  if (!tenant) return <p className="muted">Carregando…</p>;

  const sub = subscriptionBadge(tenant.subscriptionStatus);
  // Assinatura vigente com recorrência no gateway: é o que a API aceita cancelar.
  const cancellableSubscription = Boolean(tenant.billing?.asaasSubscriptionId);

  return (
    <div className="page page-detail" style={{ gap: 18 }}>
      <Link to="/tenants" className="backlink">← Lojas</Link>

      {reloadError && (
        <div className="error-banner" role="alert" data-testid="tenant-reload-error">
          Não foi possível atualizar os dados da loja ({reloadError}). O que aparece abaixo pode estar
          desatualizado: recarregue a página para ver a situação atual.
        </div>
      )}

      <div className="detail-header">
        <div className="detail-identity">
          <Avatar name={tenant.tradeName} size="lg" square />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h2>{tenant.tradeName}</h2>
              <Badge tone={sub.tone} dot>{sub.label}</Badge>
              {!tenant.isActive && <Badge tone="error">Inativo</Badge>}
            </div>
            <div className="detail-meta">
              {tenant.ownerName} · <span className="mono">{tenant.slug}</span> · {tenant.phone}
            </div>
            {/* O id da loja é o que o suporte cola no Hermes e nas consultas do MCP
                (change platform-mcp-server, S-2.17). */}
            <div className="detail-meta" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className="mono" data-testid="tenant-id">{tenant.id}</span>
              <CopyButton value={tenant.id} label="Copiar id da loja" />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to={`/orders/search?mode=tenant&tenantId=${tenant.id}`} className="btn btn-secondary btn-sm">
            <PackageSearch size={16} /> Ver pedidos
          </Link>
          {/* Was labelled "Concessões" while pointing at adjustments. Now that
              access concessions exist as their own thing, the old label would
              send an operator looking to unblock a store into a screen that
              only records money. */}
          <Link to={`/tenants/${tenant.id}/adjustments`} className="btn btn-secondary btn-sm">
            <HandCoins size={16} /> Ajustes
          </Link>
          <Link to={`/tenants/${tenant.id}/concessions`} className="btn btn-secondary btn-sm">
            <KeyRound size={16} /> Concessões
          </Link>
          <Link to={`/tenants/${tenant.id}/discounts`} className="btn btn-secondary btn-sm">
            <BadgePercent size={16} /> Descontos
          </Link>
          {/* Ajustes e Descontos ficam: a listagem é de todo operador, e a
              própria tela esconde o que o perfil não pode criar. O CTA de
              trial é só ação, então some para quem não estende. */}
          {canExtendTrial && (
            <Link to={`/tenants/${tenant.id}/trial`} className="btn btn-sm">
              <CalendarPlus size={16} /> Estender teste grátis
            </Link>
          )}
          {/* Ordem de serviço para o agente de escrita (change platform-mcp-writes, 1.17): para quem
              tem a permissão de alguma ação que já vira OS; a API confere a do tipo escolhido. */}
          {canDelegateToAgent && (
            <Link to={`/tenants/${tenant.id}/agente`} className="btn btn-secondary btn-sm">
              <Bot size={16} /> Delegar ao agente
            </Link>
          )}
          {isAdmin && (
            <Link to={`/tenants/${tenant.id}/lifecycle`} className="btn btn-secondary btn-sm">
              <Power size={16} /> Ciclo de vida
            </Link>
          )}
        </div>
      </div>

      {/* Cardápio digital: o link que o operador precisa mandar para o cliente.
          Vem pronto do servidor — montar "{slug}.vaptmenu.com.br" aqui abriria o
          domínio errado em staging. */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Store size={18} />
          <div className="card-title">Cardápio digital</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <a
            href={tenant.menuUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mono"
            style={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-all' }}
          >
            {tenant.menuUrl}
          </a>
          <CopyButton value={tenant.menuUrl} label="Copiar link do cardápio" />
          <a
            href={tenant.menuUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary btn-sm"
          >
            <ExternalLink size={15} /> Abrir
          </a>
        </div>
      </div>

      <div className="stat-grid">
        <StatCard
          label="Pedidos totais"
          value={tenant.orderCount.toLocaleString('pt-BR')}
          delta={`${tenant.orderCountLast30Days.toLocaleString('pt-BR')} nos últimos 30 dias`}
          deltaTone={tenant.orderCountLast30Days > 0 ? 'success' : 'neutral'}
          icon={<PackageOpen size={22} />}
        />
        <StatCard
          label="Último pedido"
          value={
            tenant.lastOrderAt
              ? new Date(tenant.lastOrderAt).toLocaleDateString('pt-BR')
              : 'Nunca'
          }
          delta={
            tenant.lastOrderAt
              ? new Date(tenant.lastOrderAt).toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : 'sem pedidos registrados'
          }
          deltaTone="neutral"
          icon={<PackageSearch size={22} />}
        />
        <StatCard
          label="Loja agora"
          value={tenant.isStoreOpen ? 'Aberta' : 'Fechada'}
          delta="conforme horários configurados"
          deltaTone={tenant.isStoreOpen ? 'success' : 'neutral'}
          icon={tenant.isStoreOpen ? <DoorOpen size={22} /> : <DoorClosed size={22} />}
        />
        <StatCard
          label="Mensalidade"
          value={tenant.billing?.value != null ? `R$ ${tenant.billing.value.toFixed(2)}` : '—'}
          delta={tenant.billing ? `Asaas ${tenant.billing.status}` : 'sem subscription'}
          deltaTone="neutral"
          icon={<Wallet size={22} />}
        />
        <StatCard
          label="Criado em"
          value={new Date(tenant.createdAt).toLocaleDateString('pt-BR')}
          icon={<CalendarPlus size={22} />}
        />
      </div>

      <div className="grid-halves">
        <div className="card">
          <div className="card-title" style={{ marginBottom: 14 }}>Conta</div>
          <div className="detail-grid">
            <div className="k">Responsável</div><div>{tenant.ownerName}</div>
            {/* "—" explícito quando não há dono ativo: um tenant órfão é
                exatamente o que o suporte precisa enxergar. */}
            <div className="k">Email</div>
            <div>
              {tenant.ownerEmail
                ? <a href={`mailto:${tenant.ownerEmail}`}>{tenant.ownerEmail}</a>
                : <span className="muted">—</span>}
            </div>
            <div className="k">Telefone</div><div>{tenant.phone}</div>
            <div className="k">Documento</div>
            <div className="mono">
              {tenant.cnpjMasked ?? tenant.cpfMasked ?? <span className="muted">—</span>}
            </div>
            <div className="k">Endereço</div>
            <div>{formatAddress(tenant.address)}</div>
            <div className="k">Assinatura</div><div><Badge tone={sub.tone}>{sub.label}</Badge></div>
            <div className="k">Ativo</div><div>{tenant.isActive ? 'Sim' : 'Não'}</div>
            <div className="k">Teste grátis expira</div>
            <div>{tenant.trialExpiresAt ? new Date(tenant.trialExpiresAt).toLocaleString('pt-BR') : '—'}</div>
            <div className="k">Criado em</div>
            <div>{new Date(tenant.createdAt).toLocaleString('pt-BR')}</div>
            {/* Somente leitura: gravada no cadastro a partir dos utm do link. */}
            <div className="k">Origem do cadastro</div>
            <div>
              {tenant.acquisition ? describeAcquisition(tenant.acquisition) : <span className="muted">{describeAcquisition(null)}</span>}
              {(tenant.acquisition?.content || tenant.acquisition?.term) && (
                <div className="muted mono" style={{ fontSize: 12 }}>
                  {[tenant.acquisition?.content, tenant.acquisition?.term].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <CreditCard size={18} />
            <div className="card-title">Faturamento · Asaas</div>
          </div>
          {tenant.billing ? (
            <div className="detail-grid">
              <div className="k">Subscription</div>
              <div className="mono">{tenant.billing.asaasSubscriptionId ?? '—'}</div>
              <div className="k">Status Asaas</div>
              <div><Badge tone="info">{tenant.billing.status}</Badge></div>
              <div className="k">Valor</div>
              <div style={{ fontWeight: 600 }}>
                {tenant.billing.value !== null ? `R$ ${tenant.billing.value.toFixed(2)}` : '—'}
              </div>
              <div className="k">Próx. vencimento</div>
              <div>{tenant.billing.nextDueDate ?? '—'}</div>
            </div>
          ) : (
            <p className="muted" style={{ fontSize: 14, margin: 0 }}>Sem subscription ativa.</p>
          )}
          {/* Só Admin, e só com assinatura vigente: os outros papéis nem veem a ação. */}
          <SubscriptionCancelPanel
            tenantId={tenant.id}
            tradeName={tenant.tradeName}
            visible={isAdmin && cancellableSubscription}
            onSettled={() => setReloadTick((t) => t + 1)}
          />
        </div>
      </div>

      {/* Plano efetivo e cortesia de plano: a seção some sozinha para o suporte,
          que não tem as rotas (Commercial e Admin). */}
      <TenantPlanSection tenantId={tenant.id} />

      {/* Atendimentos do Atendente IA: carteira, cortesia, estorno e reembolso. Some para o
          suporte, como o plano (Commercial e Admin). */}
      <TenantAiUnitsSection tenantId={tenant.id} tenantActive={tenant.isActive} tradeName={tenant.tradeName} />

      {/* Impressão nos pontos de produção, só leitura: o suporte vê a situação
          e orienta o dono, que é quem pausa, retoma ou desativa. */}
      <TenantPrintingSection tenantId={tenant.id} tenantActive={tenant.isActive} />
    </div>
  );
}

/** O mesmo intervalo do `CancelSubscriptionRequest` na API. */
const CANCEL_REASON_MIN = 3;
const CANCEL_REASON_MAX = 500;

const saoPauloDateTime = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  dateStyle: 'short',
  timeStyle: 'short',
});

/**
 * Cancelamento da assinatura no gateway (só Admin, com step-up, change
 * platform-backoffice-hardening, C6). Irreversível: o gateway não restaura a assinatura
 * cancelada, e restabelecer a cobrança exige criar uma nova. Pede motivo, confirmação explícita
 * e o código do autenticador.
 *
 * A chave da tentativa (`useIdempotencyKey`) se repete depois do 403 de step-up e depois de uma
 * falha do gateway (5xx ou sem resposta): a API libera a reserva no 5xx, e a mesma chave retoma
 * o cancelamento em vez de começar outro. O 202 (reconciliação financeira em processamento) é
 * aceito.
 */
function SubscriptionCancelPanel({
  tenantId,
  tradeName,
  visible,
  onSettled,
}: {
  tenantId: string;
  tradeName: string;
  visible: boolean;
  onSettled: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const idempotency = useIdempotencyKey();

  const trimmed = reason.trim();
  const reasonValid = trimmed.length >= CANCEL_REASON_MIN && trimmed.length <= CANCEL_REASON_MAX;
  const canSubmit = confirmed && reasonValid;

  /**
   * O cancelamento com o código do step-up. O motivo é o que o operador confirmou ao abrir o modal
   * (`startCancel`), congelado pelo `useStepUpDialog`, e não o estado vivo do formulário.
   */
  async function cancelSubscription(proof: StepUpProof, confirmedReason: string) {
    const body = { reason: confirmedReason };
    const key = idempotency.keyFor(cancelTenantSubscriptionUrl(tenantId), body);
    let data;
    try {
      data = await cancelTenantSubscription(tenantId, confirmedReason, { idempotencyKey: key, ...stepUpOptions(proof) });
      idempotency.settle();
    } catch (err) {
      idempotency.settle(err);
      throw err;
    }
    setOpen(false);
    setReason('');
    setConfirmed(false);
    setNotice(
      data.cancellationStatus === 'reconciliation_pending'
        ? 'Cancelamento aceito: a recorrência foi interrompida no gateway e a reconciliação financeira está em processamento.'
        : data.paidThroughEndsAt
          ? `Assinatura cancelada. O acesso segue até ${saoPauloDateTime.format(new Date(data.paidThroughEndsAt))}, fim do período pago.`
          : 'Assinatura cancelada. O acesso segue até o fim do período pago.',
    );
    onSettled();
  }

  const stepUp = useStepUpDialog(cancelSubscription, (err) => setError(describeError(err)), {
    // O canSubmit de novo no envio do código, sobre o motivo congelado.
    isValid: (confirmedReason) =>
      confirmed && confirmedReason.length >= CANCEL_REASON_MIN && confirmedReason.length <= CANCEL_REASON_MAX,
    // Passkey (C14): o desafio preso a este tenant.
    passkeyTarget: () => stepUpTarget('subscriptionCancel', tenantId),
  });

  function startCancel(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit || stepUp.open) return;
    setError(null);
    stepUp.start(trimmed);
  }

  if (!visible && !notice) return null;

  return (
    <div style={{ marginTop: 14 }}>
      {notice && (
        <div className="info-banner" role="status">
          {notice}
        </div>
      )}

      {visible && !open && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(true)}>
          <Ban size={15} /> Cancelar assinatura
        </button>
      )}

      {visible && open && (
        <form onSubmit={startCancel} aria-label="Cancelar assinatura" data-testid="subscription-cancel">
          {/* Com o modal do step-up aberto, motivo, confirmação e botões ficam travados: o que sai
              é o que o operador confirmou ao abrir o modal. */}
          <fieldset disabled={stepUp.open} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <div
              className="error-banner"
              role="note"
              data-testid="subscription-cancel-warning"
            >
              <strong>Irreversível.</strong> O gateway não restaura uma assinatura cancelada: restabelecer
              a cobrança exige criar uma assinatura nova. A loja segue no ar até o fim do período já pago;
              para tirá-la do ar agora, a ação é a desativação.
            </div>
  
            <div className="field">
              <label htmlFor="subscription-cancel-reason">Motivo do cancelamento</label>
              <textarea
                id="subscription-cancel-reason"
                rows={2}
                maxLength={CANCEL_REASON_MAX}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
  
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              <span>
                Entendo que cancelar a assinatura de <strong>{tradeName}</strong> não tem volta.
              </span>
            </label>
  
            {error && (
              <div className="error-banner" role="alert">
                {error}
              </div>
            )}
  
            <div className="btn-row">
              <button type="submit" className="btn btn-danger btn-sm" disabled={!canSubmit || stepUp.busy}>
                {stepUp.busy ? 'Cancelando…' : 'Cancelar assinatura'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={stepUp.busy}
                onClick={() => {
                  setOpen(false);
                  setError(null);
                }}
              >
                Voltar
              </button>
            </div>
          </fieldset>
        </form>
      )}

      {stepUp.open && (
        <StepUpDialog
          title="Cancelar assinatura"
          description={
            <>
              <p style={{ margin: '0 0 8px' }}>
                A recorrência de <strong>{tradeName}</strong> será encerrada no gateway agora. O gateway
                não restaura a assinatura: restabelecer a cobrança exige criar uma nova.
              </p>
              <p style={{ margin: 0 }}>A loja segue no ar até o fim do período já pago.</p>
            </>
          }
          busy={stepUp.busy}
          error={stepUp.error}
          onSubmit={stepUp.submit}
          onCancel={stepUp.cancel}
          onPasskey={stepUp.passkeyAvailable ? stepUp.submitPasskey : undefined}
        />
      )}
    </div>
  );
}
